import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { FocusIcon } from '@atlaskit/logo';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/compiled';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type { searchMercuryFocusArea_AtlassianSearchPage$key } from './__generated__/searchMercuryFocusArea_AtlassianSearchPage.graphql';
import { SearchMercuryFocusAreaStatusTargetDate } from './search-mercury-focus-area-status-target-date';

export const SearchMercuryFocusArea = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchResultProps<searchMercuryFocusArea_AtlassianSearchPage$key> &
	ResultDerivedDataProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchMercuryFocusArea_AtlassianSearchPage on SearchResult {
				...searchMercuryFocusAreaStatusTargetDate_AtlassianSearchPage
				__typename

				id
				title
				type
				url

				... on SearchResultMercuryFocusArea {
					lastModifiedDate
					focusArea {
						id
						name
						focusAreaType {
							id
							name
						}

						owner {
							name
						}
					}
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type, cloudId, selectedSites]);

	const title = useMemo(() => enrichExcerpt(data.title), [data.title]);
	const headerMetadataParts = useMemo(
		() => [
			[
				'type',
				<Box>
					<SearchResultType type={data.type as SearchClientResultType} />
				</Box>,
			],
			['focusAreaType', <Box>{data.focusArea?.focusAreaType?.name}</Box>],
			data.focusArea?.owner?.name ? ['owner', <Box>{data.focusArea?.owner?.name}</Box>] : null,
			[
				'last-modified',
				data.lastModifiedDate ? (
					<Box>
						<LastModified lastModified={data.lastModifiedDate} />
					</Box>
				) : null,
			],
		],
		[
			data.type,
			data.focusArea?.focusAreaType?.name,
			data.focusArea?.owner?.name,
			data.lastModifiedDate,
		],
	);

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			}) => (
				<SearchResult
					productConfigs={productConfigs}
					{...data}
					product={ProductKeys.Focus}
					productSelected={!!selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					url={data.url}
					title={title}
					icon={<FocusIcon size="medium" />}
					headerMetadataParts={headerMetadataParts}
					description={<SearchMercuryFocusAreaStatusTargetDate node={data} />}
					cloudId={cloudId}
					position={position}
					partial={data}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</ResultAnalytics>
	);
};

export const isSearchMercuryFocusArea = (
	node?: any,
): node is searchMercuryFocusArea_AtlassianSearchPage$key => {
	return node?.id?.includes('mercury') && node.__typename === 'SearchResultMercuryFocusArea';
};
